import { createFileRoute } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { FaqSourceNote } from "@/components/FaqSourceNote";
import { SiteFooter } from "@/components/SiteFooter";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";
import { NAATI_FAQ_CATEGORIES } from "@/content/naati-corpus/faq-defs";

export const Route = createFileRoute("/en/naati/faq/")({
  loader: async (): Promise<{ NAATI_FAQ_EN: { q: string; a: string }[]; NAATI_KEYWORDS_EN: string[] }> => {
    const [{ NAATI_FAQ_EN }, { NAATI_KEYWORDS_EN }] = await Promise.all([
      import("@/content/naati-corpus/faq-en"),
      import("@/content/naati-corpus/keywords-en"),
    ]);
    return { NAATI_FAQ_EN, NAATI_KEYWORDS_EN };
  },
  component: NaatiFaqHubEn,
  head: ({ loaderData }) => {
    const { NAATI_FAQ_EN = [], NAATI_KEYWORDS_EN = [] } = loaderData ?? {};
    const site = getActiveSiteUrl();
    return {
      ...pageHeadForProvince({
        title: `NAATI Certified Translation FAQ — ${NAATI_FAQ_EN.length} Questions (2026)`,
        description:
          "Complete FAQ on NAATI certified translation for Australian/NZ visas — civil registry documents, education, financial evidence, partner visas, student/skilled visas, pricing and coverage. Updated 2026.",
        path: "/en/naati/faq",
        type: "article",
        alternates: [{ lang: "th", path: "/naati/faq" }],
        keywords: NAATI_KEYWORDS_EN.slice(0, 80),
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "Home", url: `${site}/en` },
              { name: "NAATI Translation", url: `${site}/en/naati` },
              { name: "NAATI FAQ", url: `${site}/en/naati/faq` },
            ]),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(NAATI_FAQ_EN)) },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            speakable: { "@type": "SpeakableSpecification", cssSelector: ["h1", "h2", ".faq-answer"] },
            keywords: NAATI_KEYWORDS_EN.slice(0, 100).join(", "),
          }),
        },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema("/en/naati/faq")) },
      ],
    };
  },
});

function NaatiFaqHubEn() {
  const { NAATI_FAQ_EN, NAATI_KEYWORDS_EN } = Route.useLoaderData() as { NAATI_FAQ_EN: { q: string; a: string }[]; NAATI_KEYWORDS_EN: string[] };
  return (
    <>
      <SiteHeader />
      <main className="container mx-auto max-w-4xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/en" className="hover:underline">Home</a> <span>/</span>{" "}
          <a href="/en/naati" className="hover:underline">NAATI Translation</a> <span>/</span> <span>FAQ</span>
        </nav>
        <h1 className="text-3xl md:text-4xl font-bold mb-3">
          NAATI Certified Translation — FAQ {NAATI_FAQ_EN.length} Questions (2026)
        </h1>
        <p className="text-muted-foreground mb-8">
          Everything about NAATI certified translation for Australia, New Zealand, Canada and UK visas —
          civil registry documents, education, financial evidence, relationship evidence, pricing and nationwide coverage.
        </p>

        <div className="mb-6 rounded-lg border bg-muted/30 p-4 text-sm">
          <strong>ภาษาไทย:</strong>{" "}
          <a href="/naati/faq" className="text-primary underline">
            คำถามที่พบบ่อยเรื่องแปล NAATI →
          </a>
        </div>

        <section aria-label="NAATI FAQ categories" className="mb-8">
          <h2 className="text-lg font-semibold mb-3">Categories</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {NAATI_FAQ_CATEGORIES.map((c) => (
              <a
                key={c.slug}
                href={`/en/naati/faq/${c.slug}`}
                className="rounded-lg border bg-card p-3 text-sm transition-colors hover:border-primary hover:bg-primary/5"
              >
                <div className="font-semibold mb-1">{c.titleEn} →</div>
                <div className="text-xs text-muted-foreground line-clamp-2">{c.descEn}</div>
              </a>
            ))}
          </div>
        </section>

        <div className="space-y-4">
          {NAATI_FAQ_EN.map((item, i) => (
            <details key={i} className="rounded-lg border bg-card p-4 open:shadow-sm" {...(i < 5 ? { open: true } : {})}>
              <summary className="cursor-pointer font-semibold">
                <span className="text-muted-foreground mr-2">Q{i + 1}.</span>{item.q}
              </summary>
              <p className="faq-answer mt-3 text-sm leading-relaxed text-muted-foreground">{item.a}</p>
            </details>
          ))}
        </div>

        <section className="mt-12 rounded-lg border p-6 bg-primary/5">
          <h2 className="text-xl font-bold mb-2">Let our NAATI translation team handle the full workflow</h2>
          <p className="text-sm text-muted-foreground mb-4">
            Receive documents → NAATI certified translation → QA → PDF/courier delivery → lodge with DHA / INZ / IRCC
          </p>
          <a href="/contact" className="inline-block rounded-md bg-primary px-5 py-2 text-primary-foreground font-medium">
            Get a quote →
          </a>
        </section>

        <section className="mt-12">
          <h2 className="text-xl font-bold mb-3">Related keywords ({NAATI_KEYWORDS_EN.length})</h2>
          <div className="flex flex-wrap gap-2 text-xs">
            {NAATI_KEYWORDS_EN.slice(0, 300).map((kw, i) => (
              <span key={i} className="rounded-full border bg-muted/40 px-3 py-1 text-muted-foreground">{kw}</span>
            ))}
          </div>
        </section>

        <FaqSourceNote topic="translation" locale="en" />
      </main>
      <SiteFooter />
    </>
  );
}
